import { useEffect, useMemo, useState } from 'react'
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts'
import { DAYS, svcCapacity, perDay, gbpK, gbp0, num0, priorSeason, type ExportPayload } from './researchShared'
import { buildSheetSlices, weeksIn, topServices, splitSvc, fetchExport, type Slice } from './researchExportData'

// The two per-month charts that sit under the weekday cards.
//
// Chart 1 — top ten services for the chosen cut: revenue per running day
//   (this year vs last), with seats offered and load % as lines.
// Chart 2 — the same three measures across Mon..Sun, for all services or one
//   chosen service, so you can see which weekday a given departure earns on.
//
// Both read the bulk export payload, fetched ONCE per month and only when the
// user opens the charts. That endpoint is the same one the XLS/PDF downloads
// use, so opening the charts costs nothing extra if a download follows.

const GOLD = '#c89b3c'
const DEEP = '#0b3d2e'
const LY = '#b9a97f'
const CAP = '#6b8fb5'
const LOAD = '#b23b2e'

const selectStyle: React.CSSProperties = {
  border: '1px solid #cfc7b4', borderRadius: 6, padding: '3px 8px',
  fontSize: '0.75rem', background: '#fff', color: '#0b3d2e', fontWeight: 600,
}

/** Seats offered per running day across the slice's services. */
const sliceCapacity = (s: Slice): number =>
  s.services.reduce((t, svc) => t + svcCapacity(splitSvc(svc.label).service), 0)

function Panel({ title, sub, children, controls }: {
  title: string; sub: string; children: React.ReactNode; controls?: React.ReactNode
}) {
  return (
    <div style={{ border: '1px solid #e5e1d6', borderRadius: 8, padding: '10px 12px 4px', background: '#fff', flex: '1 1 460px', minWidth: 380 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.82rem', color: DEEP }}>{title}</div>
          <div style={{ fontSize: '0.68rem', color: '#8a8a8a' }}>{sub}</div>
        </div>
        {controls && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{controls}</div>}
      </div>
      {children}
    </div>
  )
}

export function ResearchMonthCharts({ month, monthLabel }: { month: number; monthLabel: string }) {
  const [open, setOpen] = useState(false)
  const [payload, setPayload] = useState<ExportPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Chart 1 controls
  const [scope, setScope] = useState<'month' | 1 | 2 | 3 | 4>('month')
  const [dayFilter, setDayFilter] = useState<number>(0) // 0 = all weekdays
  // Chart 2 controls
  const [service, setService] = useState<string>('__all__')

  useEffect(() => {
    if (!open || payload || loading) return
    setLoading(true)
    fetchExport('month', month)
      .then((p) => { setPayload(p); setError(null) })
      .catch((e) => setError(String((e as Error)?.message ?? e)))
      .finally(() => setLoading(false))
  }, [open, payload, loading, month])

  const weeks = useMemo(() => (payload ? weeksIn(payload) : []), [payload])
  const season = payload?.season ?? ''
  const lastSeason = season ? priorSeason(season) : ''

  // ---- Chart 1: top ten services for the chosen cut -----------------------
  const topData = useMemo(() => {
    if (!payload) return []
    const filter = scope === 'month' ? {} : { wom: scope }
    const { days, rollup } = buildSheetSlices(payload, filter, 'cut')
    const slice = dayFilter === 0 ? rollup : days.find((d) => d.dayNo === dayFilter)
    if (!slice) return []
    return topServices(slice, 10).map((t) => {
      const { direction, depTime, service: svc } = splitSvc(t.label)
      const cap = svcCapacity(svc)
      const pax = perDay(t.curQty, slice.curDays)
      return {
        name: `${direction === 'Northbound' ? 'N' : 'S'} ${depTime}`,
        full: svc || '(standard service)',
        revenue: Math.round(t.curPerDay),
        revenuePrev: slice.prevDays > 0 ? Math.round(t.prevPerDay) : null,
        capacity: cap,
        load: cap > 0 ? Math.round((pax / cap) * 100) : 0,
      }
    })
  }, [payload, scope, dayFilter])

  // ---- Chart 2: the three measures across Mon..Sun ------------------------
  const serviceOptions = useMemo(() => {
    if (!payload) return []
    const { rollup } = buildSheetSlices(payload, {}, 'all')
    return rollup.services
      .map((s) => ({ key: s.label, ...splitSvc(s.label), net: s.curNet }))
      .sort((a, b) => b.net - a.net)
  }, [payload])

  const weekdayData = useMemo(() => {
    if (!payload) return []
    const { days } = buildSheetSlices(payload, {}, 'all')
    return DAYS.map((label, i) => {
      const slice = days.find((d) => d.dayNo === i + 1)
      if (!slice) return { name: label, revenue: 0, revenuePrev: null, capacity: 0, load: 0, ran: false }

      if (service === '__all__') {
        const cap = sliceCapacity(slice)
        const pax = perDay(slice.totals.curQty, slice.curDays)
        return {
          name: label,
          revenue: Math.round(perDay(slice.totals.curNet, slice.curDays)),
          revenuePrev: slice.prevDays > 0 ? Math.round(perDay(slice.totals.prevNet, slice.prevDays)) : null,
          capacity: cap,
          load: cap > 0 ? Math.round((pax / cap) * 100) : 0,
          ran: true,
        }
      }

      const svc = slice.services.find((s) => s.label === service)
      if (!svc) return { name: label, revenue: 0, revenuePrev: null, capacity: 0, load: 0, ran: false }
      const cap = svcCapacity(splitSvc(service).service)
      const pax = perDay(svc.curQty, slice.curDays)
      return {
        name: label,
        revenue: Math.round(perDay(svc.curNet, slice.curDays)),
        revenuePrev: slice.prevDays > 0 ? Math.round(perDay(svc.prevNet, slice.prevDays)) : null,
        capacity: cap,
        load: cap > 0 ? Math.round((pax / cap) * 100) : 0,
        ran: true,
      }
    })
  }, [payload, service])

  const tip = (v: unknown, n: string) => {
    const num = Number(v)
    if (n === 'load') return [`${num}%`, 'load']
    if (n === 'capacity') return [num0(num), 'seats offered / day']
    return [gbp0(num), n === 'revenuePrev' ? `${lastSeason} £ / day` : `${season} £ / day`]
  }

  if (!open) {
    return (
      <div style={{ margin: '4px 0 0' }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{ ...selectStyle, cursor: 'pointer', borderColor: DEEP }}
          title="Revenue, capacity and load by service and by weekday for this month"
        >▸ Show {monthLabel} charts</button>
      </div>
    )
  }

  return (
    <div style={{ margin: '6px 0 0' }}>
      <button
        type="button"
        onClick={() => setOpen(false)}
        style={{ ...selectStyle, cursor: 'pointer', borderColor: DEEP, marginBottom: 8 }}
      >▾ Hide {monthLabel} charts</button>

      {loading && <p style={{ fontSize: '0.8rem', color: '#6b6b6b' }}>Loading {monthLabel} service data…</p>}
      {error && <p style={{ fontSize: '0.8rem', color: '#a33' }}>{error}</p>}

      {payload && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Panel
            title={`${monthLabel} — top ten services`}
            sub={`Net £ per running day, seats offered and load. ${season} vs ${lastSeason}.`}
            controls={
              <>
                <select style={selectStyle} value={String(scope)} onChange={(e) => setScope(e.target.value === 'month' ? 'month' : Number(e.target.value) as 1 | 2 | 3 | 4)}>
                  <option value="month">Whole month</option>
                  {weeks.map((w) => <option key={w} value={w}>Week {w}</option>)}
                </select>
                <select style={selectStyle} value={dayFilter} onChange={(e) => setDayFilter(Number(e.target.value))}>
                  <option value={0}>All weekdays</option>
                  {DAYS.map((d, i) => <option key={d} value={i + 1}>{d}</option>)}
                </select>
              </>
            }
          >
            {topData.length === 0 ? (
              <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b0a89a', fontSize: '0.8rem' }}>
                no running services in this cut
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={topData} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-35} textAnchor="end" height={48} />
                  <YAxis yAxisId="l" tickFormatter={(v) => gbpK(Number(v))} tick={{ fontSize: 10 }} width={52} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} width={40} domain={[0, 'dataMax']} />
                  <Tooltip
                    formatter={tip}
                    labelFormatter={(l) => topData.find((d) => d.name === l)?.full ?? String(l)}
                    contentStyle={{ fontSize: 11 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar yAxisId="l" dataKey="revenue" name={`${season} £/day`} fill={GOLD} radius={[2, 2, 0, 0]} />
                  <Bar yAxisId="l" dataKey="revenuePrev" name={`${lastSeason} £/day`} fill={LY} radius={[2, 2, 0, 0]} />
                  <Line yAxisId="r" type="monotone" dataKey="capacity" name="seats offered" stroke={CAP} strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="r" type="monotone" dataKey="load" name="load %" stroke={LOAD} strokeWidth={2} strokeDasharray="4 2" dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <Panel
            title={`${monthLabel} — by weekday`}
            sub="Revenue, seats offered and load across Mon–Sun. Pick a single service to see its own weekday shape."
            controls={
              <select style={{ ...selectStyle, maxWidth: 260 }} value={service} onChange={(e) => setService(e.target.value)}>
                <option value="__all__">All services</option>
                {serviceOptions.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.direction === 'Northbound' ? 'N' : 'S'} {s.depTime} — {s.service || '(standard)'}
                  </option>
                ))}
              </select>
            }
          >
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={weekdayData} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                <YAxis yAxisId="l" tickFormatter={(v) => gbpK(Number(v))} tick={{ fontSize: 10 }} width={52} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} width={40} domain={[0, 'dataMax']} />
                <Tooltip formatter={tip} contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar yAxisId="l" dataKey="revenue" name={`${season} £/day`} fill={GOLD} radius={[2, 2, 0, 0]} />
                <Bar yAxisId="l" dataKey="revenuePrev" name={`${lastSeason} £/day`} fill={LY} radius={[2, 2, 0, 0]} />
                <Line yAxisId="r" type="monotone" dataKey="capacity" name="seats offered" stroke={CAP} strokeWidth={2} dot={{ r: 2 }} connectNulls />
                <Line yAxisId="r" type="monotone" dataKey="load" name="load %" stroke={LOAD} strokeWidth={2} strokeDasharray="4 2" dot={{ r: 2 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}
    </div>
  )
}
